/* eslint-disable react-hooks/rules-of-hooks */

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { CurrentClientContext } from '@tebuto/guards/current-client-context'
import RequestGate from '@tebuto/layout/RequestGate'
import {
    buildPaymentOptions,
    filterOnlinePaymentMethods,
    PaymentMethodChoice,
    PaymentPreviewNotice,
    PayPalPaymentMethodsBody,
    StripePaymentMethodsBody,
    type WidgetPaymentMethodId,
    type WidgetPaymentOption
} from '@tebuto/payment/checkout'
import { prefetchStripeJs, type StripeCheckoutSubmitState } from '@tebuto/payment/stripe/StripeCheckout'
import StripeCheckoutReturnConfirming from '@tebuto/payment/stripe/StripeCheckoutReturnConfirming'
import { useWidgetStripeCheckoutSession } from '@tebuto/payment/stripe/use-widget-stripe-checkout-session'
import Heading from '@tebuto/typography/Heading'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import BookingBackButton from './BookingBackButton'
import { BookingContext } from './context'
import '@tebuto/payment/checkout/checkout.css'
import { clearPendingCheckout } from './payment/pending-checkout-storage'
import { NativeOnlinePaymentAction } from './payment/SelectedPaymentAction'
import { useWidgetStripeCheckoutReturn } from './payment/use-widget-stripe-checkout-return'
import {
    type Booking,
    type EventLocationSelection,
    type OnlinePaymentMethods,
    PaymentType,
    type PaymentTypes
} from './types'

export default function Payment() {
    const { t } = useComponentTranslation()
    const {
        bookingEvent,
        categories,
        bookingData,
        previewMode,
        usePaymentConfiguration,
        setState,
        setNavigationDirection
    } = useContext(BookingContext)
    const { paymentConfiguration, showLoading, error, mutate } = usePaymentConfiguration()
    const category = useMemo(
        () => (bookingEvent ? categories[bookingEvent.categoryId] : undefined),
        [bookingEvent, categories]
    )

    if (!bookingEvent || !bookingData || !category) {
        return <div>{t('booking.bookEvent.eventNotFound')}</div>
    }

    function backToBooking() {
        setNavigationDirection('back')
        setState('booking')
    }

    return (
        <div className="flex min-w-0 w-full flex-col gap-4" data-testid="booking-payment">
            <BookingBackButton onClick={backToBooking} label={t('back')} />
            <RequestGate data={paymentConfiguration} showLoading={showLoading} error={error} retry={mutate}>
                {(config) => <PaymentMethodsList previewMode={previewMode} {...config} />}
            </RequestGate>
        </div>
    )
}

function PaymentMethodsList({
    previewMode = false,
    paymentTypes,
    onlinePaymentMethods,
    onlineProviderType,
    stripeConnectedAccountId,
    stripePublishableKey
}: {
    previewMode?: boolean
    paymentTypes: PaymentTypes
    onlinePaymentMethods: OnlinePaymentMethods
    onlineProviderType?: 'paypal' | 'stripe' | null
    stripeConnectedAccountId?: string | null
    stripePublishableKey?: string | null
}) {
    const { t } = useComponentTranslation()
    const {
        bookingEvent,
        bookingData,
        useCreateCheckoutSession,
        useBookEvent,
        setBookingResult,
        setState,
        setNavigationDirection
    } = useContext(BookingContext)
    const clientWhoAmI = useContext(CurrentClientContext)
    const { createCheckoutSession } = useCreateCheckoutSession()
    const { bookEvent, isLoading: isBookingEventLoading } = useBookEvent()
    const [selectedMethodId, setSelectedMethodId] = useState<WidgetPaymentMethodId | null>(null)
    const [processingMethodId, setProcessingMethodId] = useState<WidgetPaymentMethodId | null>(null)
    const [isOnlineProcessing, setOnlineProcessing] = useState(false)
    const [stripeSubmitState, setStripeSubmitState] = useState<StripeCheckoutSubmitState | null>(null)

    const hasOnlinePayment = paymentTypes.includes('online')
    const isStripeProvider = onlineProviderType === 'stripe'
    const isPayPalProvider = onlineProviderType === 'paypal'
    const hasNativeOnlinePayment =
        hasOnlinePayment && (onlineProviderType === 'stripe' || onlineProviderType === 'paypal')

    const filteredOnlinePaymentMethods = useMemo(
        () => filterOnlinePaymentMethods(onlinePaymentMethods, isStripeProvider),
        [onlinePaymentMethods, isStripeProvider]
    )

    const paymentOptions: WidgetPaymentOption[] = useMemo(
        () =>
            buildPaymentOptions({
                paymentTypes,
                onlinePaymentMethods,
                isStripeProvider,
                methodCopy: {
                    paypal: {
                        label: 'PayPal',
                        description: t('booking.payment.paypalDescription')
                    },
                    google_pay: {
                        label: 'Google Pay',
                        description: t('booking.payment.googlePayDescription')
                    },
                    apple_pay: {
                        label: 'Apple Pay',
                        description: t('booking.payment.applePayDescription')
                    },
                    card: {
                        label: t('creditCard'),
                        description: t('booking.payment.cardDescription')
                    },
                    klarna: {
                        label: 'Klarna',
                        description: t('booking.payment.klarnaDescription')
                    },
                    sepa: {
                        label: 'SEPA',
                        description: t('booking.payment.sepaDescription')
                    },
                    bank_transfer: {
                        label: t('bankTransfer'),
                        description: t('booking.payment.bankTransferOnlineDescription')
                    },
                    cash: { label: 'Barzahlung vor Ort' },
                    invoice: { label: 'Auf Rechnung' }
                }
            }),
        [paymentTypes, onlinePaymentMethods, isStripeProvider, t]
    )

    useEffect(() => {
        if (paymentOptions.length === 0) {
            setSelectedMethodId(null)
            return
        }

        setSelectedMethodId((current) => {
            if (current && paymentOptions.some((option) => option.id === current)) {
                return current
            }
            return hasNativeOnlinePayment ? null : paymentOptions[0].id
        })
    }, [paymentOptions, hasNativeOnlinePayment])

    const stripeCheckoutSessionEnabled = Boolean(
        isStripeProvider &&
            stripePublishableKey &&
            stripeConnectedAccountId &&
            bookingEvent &&
            bookingData &&
            filteredOnlinePaymentMethods.length > 0
    )

    const stripeCheckoutSession = useWidgetStripeCheckoutSession({
        enabled: stripeCheckoutSessionEnabled,
        bookingEvent,
        bookingData,
        clientWhoAmI,
        createCheckoutSession
    })

    const stripeCheckoutReturn = useWidgetStripeCheckoutReturn({
        enabled: isStripeProvider,
        bookingEvent,
        bookingData,
        bookEvent,
        onBookingSuccess: (booking) => {
            setBookingResult(booking as Booking)
            clearPendingCheckout()
            setState('success')
            setNavigationDirection('forward')
        },
        onBookingFailed: () => {
            setOnlineProcessing(false)
        }
    })

    const redirectReturnErrorMessage = stripeCheckoutReturn.errorMessage

    const { resetReturnError: resetStripeReturnError } = stripeCheckoutReturn

    const handleReloadStripeCheckout = useCallback(() => {
        resetStripeReturnError()
        setStripeSubmitState(null)
        stripeCheckoutSession.reloadCheckoutSession()
    }, [resetStripeReturnError, stripeCheckoutSession])

    useEffect(() => {
        if (stripePublishableKey && stripeConnectedAccountId) {
            prefetchStripeJs(stripePublishableKey, stripeConnectedAccountId)
        }
    }, [stripePublishableKey, stripeConnectedAccountId])

    const handleStripePaymentElementChange = useCallback((event: { collapsed?: boolean }) => {
        if (event.collapsed === false) {
            setSelectedMethodId(null)
        }
    }, [])

    const handleSelectMethod = useCallback(
        (methodId: WidgetPaymentMethodId) => {
            if (processingMethodId) return
            setSelectedMethodId(methodId)
        },
        [processingMethodId]
    )

    const handleSelectOfflineMethod = useCallback(
        (methodId: WidgetPaymentMethodId) => {
            if (processingMethodId) return
            setSelectedMethodId(methodId)
        },
        [processingMethodId]
    )

    const handleShowOnlinePayment = useCallback(() => {
        if (processingMethodId || isOnlineProcessing) return
        setSelectedMethodId(null)
    }, [processingMethodId, isOnlineProcessing])

    const selectedOfflineOption = useMemo(
        () => paymentOptions.find((option) => option.id === selectedMethodId) ?? null,
        [paymentOptions, selectedMethodId]
    )

    const handleBottomCtaClick = useCallback(async (): Promise<void> => {
        if (selectedOfflineOption) {
            if (isBookingEventLoading || processingMethodId) return

            setProcessingMethodId(selectedOfflineOption.id)
            try {
                if (!bookingEvent || !bookingData) {
                    throw new Error('Booking event not found')
                }

                const paymentType = selectedOfflineOption.method === 'cash' ? PaymentType.CASH : PaymentType.INVOICE
                const booking = await bookEvent({
                    event: bookingEvent,
                    ...bookingData,
                    selectedLocation: bookingData.selectedLocation as unknown as EventLocationSelection,
                    paymentType
                })

                setBookingResult(booking)
                clearPendingCheckout()
                setState('success')
                setNavigationDirection('forward')
            } catch (error) {
                console.error('Error booking event', error)
                setState('failure')
                setNavigationDirection('forward')
            } finally {
                setProcessingMethodId(null)
            }
            return
        }

        stripeSubmitState?.submit()
    }, [
        selectedOfflineOption,
        isBookingEventLoading,
        processingMethodId,
        bookingEvent,
        bookingData,
        bookEvent,
        setBookingResult,
        setState,
        setNavigationDirection,
        stripeSubmitState
    ])

    const bottomCtaLabel = selectedOfflineOption ? t('booking.payment.completeBooking') : 'Buchung abschließen'
    const isBottomCtaLoading = selectedOfflineOption
        ? isBookingEventLoading || processingMethodId === selectedOfflineOption.id || isOnlineProcessing
        : Boolean(stripeSubmitState?.isLoading || isOnlineProcessing)
    const isBottomCtaDisabled = selectedOfflineOption
        ? isBookingEventLoading ||
          isOnlineProcessing ||
          Boolean(processingMethodId && processingMethodId !== selectedOfflineOption.id)
        : !stripeSubmitState || stripeSubmitState.disabled || isOnlineProcessing

    if (stripeCheckoutReturn.phase === 'confirming') {
        return <StripeCheckoutReturnConfirming />
    }

    if (previewMode) {
        return (
            <div className="flex flex-col gap-4">
                <PaymentMethodsHeader />
                <PaymentPreviewNotice />
                {paymentOptions.length > 0 ? (
                    <div
                        className="flex flex-col gap-3"
                        role="radiogroup"
                        aria-label={t('booking.payment.selectMethod')}
                    >
                        {paymentOptions.map((option) => (
                            <PaymentMethodChoice
                                key={option.id}
                                label={option.label}
                                description={option.description}
                                selected={selectedMethodId === option.id}
                                disabled
                                onSelect={() => undefined}
                                testId="booking-preview-payment-option"
                                badge={t('preview')}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            <PaymentMethodsHeader />

            {isPayPalProvider ? (
                <PayPalPaymentMethodsBody
                    paymentOptions={paymentOptions}
                    selectedMethodId={selectedMethodId}
                    onSelectOfflineMethod={handleSelectOfflineMethod}
                    processingMethodId={processingMethodId}
                    isOnlineProcessing={isOnlineProcessing}
                    hasOnlinePayment={hasNativeOnlinePayment}
                    onlinePaymentMethodCount={filteredOnlinePaymentMethods.length}
                    onlinePaymentSlot={
                        <NativeOnlinePaymentAction
                            onlineProviderType="paypal"
                            onlinePaymentMethods={filteredOnlinePaymentMethods}
                            stripeCheckoutSession={stripeCheckoutSession}
                            setProcessing={setOnlineProcessing}
                            isProcessing={isOnlineProcessing}
                        />
                    }
                    selectedOfflineOption={selectedOfflineOption}
                    onBottomCtaClick={() => void handleBottomCtaClick()}
                    bottomCtaDisabled={isBottomCtaDisabled}
                    bottomCtaLoading={isBottomCtaLoading}
                    bottomCtaLabel={bottomCtaLabel}
                    onShowOnlinePayment={handleShowOnlinePayment}
                />
            ) : (
                <StripePaymentMethodsBody
                    paymentOptions={paymentOptions}
                    selectedMethodId={selectedMethodId}
                    onSelectMethod={handleSelectMethod}
                    processingMethodId={processingMethodId}
                    isOnlineProcessing={isOnlineProcessing}
                    hasNativeOnlinePayment={hasNativeOnlinePayment}
                    onlinePaymentSlot={
                        hasNativeOnlinePayment && filteredOnlinePaymentMethods.length > 0 ? (
                            <NativeOnlinePaymentAction
                                onlineProviderType={onlineProviderType}
                                onlinePaymentMethods={filteredOnlinePaymentMethods}
                                stripeConnectedAccountId={stripeConnectedAccountId}
                                stripePublishableKey={stripePublishableKey}
                                stripeCheckoutSession={stripeCheckoutSession}
                                setProcessing={setOnlineProcessing}
                                isProcessing={isOnlineProcessing}
                                onStripeSubmitStateChange={setStripeSubmitState}
                                offlineMethodSelected={selectedMethodId !== null}
                                onStripePaymentElementChange={handleStripePaymentElementChange}
                            />
                        ) : null
                    }
                    redirectReturnErrorMessage={redirectReturnErrorMessage}
                    onReloadCheckoutSession={handleReloadStripeCheckout}
                    isReloadingCheckoutSession={stripeCheckoutSession.isInitializing}
                    selectedOfflineOption={selectedOfflineOption}
                    onBottomCtaClick={() => void handleBottomCtaClick()}
                    bottomCtaDisabled={isBottomCtaDisabled}
                    bottomCtaLoading={isBottomCtaLoading}
                    bottomCtaLabel={bottomCtaLabel}
                    showBottomCta={Boolean(selectedOfflineOption || hasNativeOnlinePayment)}
                />
            )}
        </div>
    )
}

function PaymentMethodsHeader() {
    const { t } = useComponentTranslation()

    return (
        <div className="space-y-1 mb-2">
            <Heading level={4} className="text-lg widget-text-primary font-semibold">
                {t('booking.payment.title')}
            </Heading>
            <p className="text-sm widget-text-secondary">{t('booking.payment.selectMethod')}</p>
        </div>
    )
}
